import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from '@/hooks/use-toast';
import { apiConfig } from '@/lib/env';
import { NotificationMessage, Notification } from '@/types/notification';
import { showNativeNotification } from '@/services/nativeNotificationService';

interface UseNotificationWebSocketOptions {
  onNotification?: (notification: Notification) => void;
  enableToast?: boolean;
  enableNativeNotifications?: boolean;
}

export const useNotificationWebSocket = (options: UseNotificationWebSocketOptions = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const { onNotification, enableToast = true, enableNativeNotifications = false } = options;
  
  const maxReconnectAttempts = 5;
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const connectionErrorShownRef = useRef<boolean>(false);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const rawListenersRef = useRef<Set<(data: any) => void>>(new Set());

  const connectWebSocket = useCallback(() => {
    // Longer initial delay to wait for auth initialization
    const connectDelay = reconnectAttempts === 0 ? 500 : 0;
    
    setTimeout(() => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.log('No auth token available for notification WebSocket');
          return;
        }

        // Prevent connection during initial auth load
        const authLoading = localStorage.getItem('authLoading') === 'true';
        if (authLoading && reconnectAttempts === 0) {
          console.log('Auth still loading, deferring notification connection');
          // Retry after auth should be ready
          setTimeout(() => connectWebSocket(), 1000);
          return;
        }

        // Prevent duplicate connections
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
          console.log('WebSocket already connected, skipping new connection');
          return;
        }

        // Close existing connection if any
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
          socketRef.current.close();
        }

        const wsUrl = `${apiConfig.liveWsUrl}/live/notifications?token=${token}`;
        console.log('Connecting to notification WebSocket:', wsUrl);
        
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('Notification WebSocket connected');
          setIsConnected(true);
          setReconnectAttempts(0);
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
          }
          pingIntervalRef.current = setInterval(() => {
            try {
              if (ws.readyState === WebSocket.OPEN) {
                ws.send(JSON.stringify({ type: 'ping' }));
                console.log('[Notification WS] keepalive ping sent');
              }
            } catch (error) {
              console.warn('Notification WebSocket ping failed:', error);
            }
          }, 25000);
        };
        
        ws.onmessage = (event) => {
          try {
            const rawData = JSON.parse(event.data);
            console.log('Received notification:', rawData);

            // Notify all raw message listeners
            rawListenersRef.current.forEach(listener => listener(rawData));

            const data: NotificationMessage = rawData;
            const isOutboundCallEvent = typeof data.type === 'string' && data.type.startsWith('outbound_call_');
            
            const notification: Notification = {
              id: data.id || `notification-${Date.now()}`,
              type: data.type,
              message: data.message,
              timestamp: data.timestamp ? new Date(data.timestamp) : new Date(),
              read: false,
              data: rawData
            };
            
            // Don't add outbound call events to the general notification list
            if (!isOutboundCallEvent) {
              setNotifications(prev => [notification, ...prev]);
              setUnreadCount(prev => prev + 1);
            }
            
            // Call custom handler if provided
            if (onNotification) {
              onNotification(notification);
            }
            
            // Skip toasts for outbound call events (they have their own UI)
            if (enableToast && !isOutboundCallEvent) {
              const type = data.type;
              switch (type) {
                case 'success':
                  toast.success(data.message);
                  break;
                case 'warning':
                  toast.warning(data.message);
                  break;
                case 'error':
                case 'critical':
                  toast.error(data.message);
                  break;
                case 'system_notification':
                case 'info':
                case 'message':
                case 'call':
                case 'email':
                default:
                  toast.info(data.message);
                  break;
              }
            }

            // Show native notification if enabled (skip outbound call events)
            if (enableNativeNotifications && !isOutboundCallEvent) {
              showNativeNotification({
                title: 'New Notification',
                body: data.message,
                tag: data.type,
                requireInteraction: ['error', 'critical', 'call'].includes(data.type)
              });
            }
          } catch (error) {
            console.error('Error parsing notification message:', error);
          }
        };
        
        ws.onclose = (event) => {
          console.log(`Notification WebSocket closed with code: ${event.code}, URL: ${wsUrl}`);
          setIsConnected(false);
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
          
          // Only show error after auth is loaded and multiple failures
          const authLoaded = localStorage.getItem('authLoading') !== 'true';
          
          // Handle authentication failures
          if (event.code === 1008 || event.code === 1006) {
            // Only show error if auth is loaded and we've tried at least twice
            if (enableToast && !connectionErrorShownRef.current && authLoaded && reconnectAttempts >= 1) {
              connectionErrorShownRef.current = true;
              toast.error("Unable to connect to notification service. Check your connection and refresh the page to retry.");
            }
            setReconnectAttempts(maxReconnectAttempts);
          } else if (reconnectAttempts < maxReconnectAttempts) {
            // Attempt to reconnect with exponential backoff
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
            reconnectTimeoutRef.current = setTimeout(() => {
              setReconnectAttempts(prev => prev + 1);
              connectWebSocket();
            }, delay);
          }
        };
        
        ws.onerror = (error) => {
          console.error('Notification WebSocket error:', error);
          setIsConnected(false);
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
          
          // Only show error after multiple failures AND auth is loaded
          const authLoaded = localStorage.getItem('authLoading') !== 'true';
          if (!connectionErrorShownRef.current && reconnectAttempts >= 2 && authLoaded && enableToast) {
            connectionErrorShownRef.current = true;
            toast.error("Unable to connect to notification service. Check your connection and refresh the page to retry.");
          }
          
          // Auto-retry on initial failures
          if (reconnectAttempts < 3) {
            const delay = (reconnectAttempts + 1) * 1000;
            reconnectTimeoutRef.current = setTimeout(() => {
              setReconnectAttempts(prev => prev + 1);
            }, delay);
          }
        };
        
        socketRef.current = ws;
      } catch (error) {
        console.error('Error connecting to notification WebSocket:', error);
        setReconnectAttempts(prev => prev + 1);
      }
    }, connectDelay);
  }, [reconnectAttempts, onNotification, enableToast, maxReconnectAttempts]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
    setIsConnected(false);
    setReconnectAttempts(0);
  }, []);

  const markAsRead = useCallback((notificationId: string) => {
    setNotifications(prev => 
      prev.map(notification =>
        notification.id === notificationId
          ? { ...notification, read: true }
          : notification
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
    setUnreadCount(0);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const addRawMessageListener = useCallback((listener: (data: any) => void) => {
    rawListenersRef.current.add(listener);
  }, []);

  const removeRawMessageListener = useCallback((listener: (data: any) => void) => {
    rawListenersRef.current.delete(listener);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      disconnect();
    };
  }, [disconnect]);

  return {
    socket: socketRef.current,
    isConnected,
    notifications,
    unreadCount,
    reconnectAttempts,
    maxReconnectAttempts,
    connectWebSocket,
    disconnect,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    addRawMessageListener,
    removeRawMessageListener
  };
};